import { KPICard } from "@/components/accounting/kpi-card";
import { ProductPicker } from "@/components/manufacturing/ProductPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useWorkspacePage } from "@/components/workspace/use-workspace-page";
import {
	useGetSopDemandPlan,
	useCheckSopCapacity,
} from "@/hooks/use-manufacturing";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Area,
	AreaChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { Search, X } from "lucide-react";

export const Route = createFileRoute("/manufacturing/sop")({
	component: SopPage,
});

function SopPage() {
	const { t } = useTranslation("manufacturing");
	const [familyProductIds, setFamilyProductIds] = useState<string[]>([]);
	const [pendingProductId, setPendingProductId] = useState("");
	const checkCapacity = useCheckSopCapacity();
	const [rccpData, setRccpData] = useState<{
		workCenters?: Array<{
			workCenterId: string;
			name?: string;
			periods?: Array<{ utilization: number }>;
		}>;
	} | null>(null);

	const handleAddProduct = useCallback(
		(id: string) => {
			if (id && !familyProductIds.includes(id)) {
				setFamilyProductIds((prev) => [...prev, id]);
			}
			setPendingProductId("");
		},
		[familyProductIds],
	);

	const handleRemoveProduct = useCallback((id: string) => {
		setFamilyProductIds((prev) => prev.filter((p) => p !== id));
	}, []);

	const { data: demandPlan } = useGetSopDemandPlan({
		familyProductIds,
		familyName: "Все товары",
		horizonMonths: 12,
	});

	const handleRecalculate = useCallback(async () => {
		if (familyProductIds.length === 0) {
			toast.info("Выберите семейство товаров для пересчёта");
			return;
		}
		if (demandPlan?.buckets) {
			try {
				const rccp = await checkCapacity.mutateAsync({
					productionPlan: demandPlan.buckets,
					familyProductIds,
					horizonMonths: 12,
				});
				setRccpData(rccp as typeof rccpData);
				toast.success("RCCP пересчитан");
			} catch {
				toast.error("Ошибка при пересчёте RCCP");
			}
		}
	}, [demandPlan, familyProductIds, checkCapacity]);

	useWorkspacePage({ statusBar: [] });

	// Prepare chart data from demand plan
	const chartData = useMemo(() => {
		if (!demandPlan?.buckets) return [];
		return demandPlan.buckets.map((b) => ({
			name: `${b.year}-${String(b.month).padStart(2, "0")}`,
			demand: b.demandQty,
			supply: b.supplyQty,
			production: b.productionQty,
			inventory: b.inventoryQty,
		}));
	}, [demandPlan]);

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="flex items-center gap-2 px-4 py-2 shrink-0">
				<div className="relative flex-1">
					<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
					<Input
						placeholder={t("search_placeholder", { defaultValue: "Поиск..." })}
						className="pl-9 h-9"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="h-9"
					onClick={() => {
						toast.success("План согласован");
					}}
				>
					{t("sop_approve_plan")}
				</Button>
				<Button size="sm" className="h-9" onClick={handleRecalculate}>
					{t("sop_recalculate")}
				</Button>
			</div>
			<div className="flex flex-col gap-3 p-3 overflow-y-auto flex-1">
			{/* KPI Summary */}
			<div className="grid grid-cols-4 gap-3">
				<KPICard
					label={t("sop_demand_plan")}
					amount={
						demandPlan?.totalDemand
							? Number(demandPlan.totalDemand).toLocaleString("ru-RU")
							: "—"
					}
				/>
				<KPICard
					label={t("sop_revenue")}
					amount={
						demandPlan?.totalRevenue
							? `₽${Number(demandPlan.totalRevenue).toLocaleString("ru-RU")}`
							: "—"
					}
				/>
				<KPICard label={t("kpi_capacity_utilization")} amount="—" />
				<KPICard label={t("sop_gross_profit")} amount="—" />
			</div>

			{/* Product Family Selector */}
			<div className="rounded-md border bg-background p-3">
				<h3 className="mb-2 text-xs font-semibold text-muted-foreground uppercase">
					Семейство товаров
				</h3>
				<div className="flex items-center gap-2 mb-2">
					<div className="w-64">
						<ProductPicker
							value={pendingProductId}
							onChange={handleAddProduct}
							placeholder="Добавить товар..."
						/>
					</div>
				</div>
				{familyProductIds.length > 0 && (
					<div className="flex flex-wrap gap-1">
						{familyProductIds.map((id) => (
							<Badge key={id} variant="secondary" className="gap-1 text-xs">
								{id.slice(0, 8)}
								<button
									type="button"
									onClick={() => handleRemoveProduct(id)}
									className="ml-0.5 hover:text-destructive"
								>
									<X className="h-3 w-3" />
								</button>
							</Badge>
						))}
					</div>
				)}
			</div>

			{/* Demand vs Supply Chart */}
			<div className="rounded-md border bg-background p-3">
				<h3 className="mb-3 text-xs font-semibold text-muted-foreground uppercase">
					{t("sop_demand_plan")} vs {t("sop_supply_plan")}
				</h3>
				{chartData.length > 0 ? (
					<ResponsiveContainer width="100%" height={250}>
						<AreaChart data={chartData}>
							<CartesianGrid strokeDasharray="3 3" className="stroke-border" />
							<XAxis
								dataKey="name"
								tick={{ fontSize: 11 }}
								className="fill-muted-foreground"
							/>
							<YAxis tick={{ fontSize: 11 }} className="fill-muted-foreground" />
							<Tooltip />
							<Area
								type="monotone"
								dataKey="demand"
								stroke="#6366f1"
								fill="#6366f1"
								fillOpacity={0.15}
								name={t("sop_demand_plan")}
							/>
							<Area
								type="monotone"
								dataKey="supply"
								stroke="#22c55e"
								fill="#22c55e"
								fillOpacity={0.15}
								name={t("sop_supply_plan")}
							/>
						</AreaChart>
					</ResponsiveContainer>
				) : (
					<div className="flex h-[250px] items-center justify-center text-xs text-muted-foreground">
						Выберите семейство товаров для отображения S&OP плана
					</div>
				)}
			</div>

			{/* RCCP Capacity Bars */}
			<div className="rounded-md border bg-background p-3">
				<h3 className="mb-3 text-xs font-semibold text-muted-foreground uppercase">
					{t("sop_capacity")}
				</h3>
				{rccpData?.workCenters && rccpData.workCenters.length > 0 ? (
					<div className="flex flex-col gap-1.5">
						{rccpData.workCenters.map((wc) => {
							const avgUtil =
								wc.periods && wc.periods.length > 0
									? wc.periods.reduce((sum, p) => sum + (p.utilization ?? 0), 0) /
										wc.periods.length
									: 0;
							return (
								<div key={wc.workCenterId} className="flex items-center gap-2 py-1">
									<span className="w-24 text-xs truncate">
										{wc.name || wc.workCenterId.slice(0, 8)}
									</span>
									<div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
										<div
											className={`h-full rounded-full ${avgUtil > 90 ? "bg-red-500" : avgUtil > 70 ? "bg-amber-500" : "bg-green-500"}`}
											style={{ width: `${Math.min(avgUtil, 100)}%` }}
										/>
									</div>
									<span className="w-12 text-xs text-right tabular-nums">
										{avgUtil.toFixed(0)}%
									</span>
								</div>
							);
						})}
					</div>
				) : (
					<div className="flex h-[200px] items-center justify-center text-xs text-muted-foreground">
						{familyProductIds.length === 0
							? "Выберите семейство товаров для отображения данных RCCP"
							: "Нажмите «Пересчитать» для загрузки данных RCCP"}
					</div>
				)}
			</div>

			{/* Gaps */}
			<div className="rounded-md border bg-background p-3">
				<h3 className="mb-3 text-xs font-semibold text-muted-foreground uppercase">
					{t("sop_gaps")}
				</h3>
				<div className="text-xs text-muted-foreground">
					Разрывы не обнаружены
				</div>
			</div>
			</div>
		</div>
	);
}
